// packages/ui/src/hooks/useCrashSafeDrafts.ts
import { useEffect, useRef } from 'react';
import type { PlatformBridge } from '@slate/core';
import { useDocStore } from '../store/docStore';
import { draftRegistry } from '../store/draftId';
import { registerFlusher } from '../session/closeController';

const DRAFT_DEBOUNCE_MS = 600;

interface DocLike {
  id: string;
  text: string;
  dirty: boolean;
  kind?: 'text' | 'image' | 'untitled';
}

/** What to write for a doc, or null when it should not produce a draft. */
export function draftWriteTargets(
  doc: DocLike,
): { draftId: string; text: string } | null {
  if (!doc.dirty) return null;
  if (doc.kind === 'image') return null;
  return { draftId: draftRegistry.idFor(doc.id), text: doc.text };
}

/** Draft ids whose docs left the dirty set that were saved or closed; delete these. */
export function diffDraftDeletions(prevDirty: Set<string>, nextDirty: Set<string>): string[] {
  const out: string[] = [];
  for (const id of prevDirty) {
    if (!nextDirty.has(id)) {
      const draftId = draftRegistry.forget(id);
      if (draftId) out.push(draftId);
    }
  }
  return out;
}

/**
 * Always-on crash-safe drafts. Independent of the autosave-to-disk setting.
 * Writes the active doc's text to a draft after ~600 ms idle and immediately on
 * blur / visibility-hidden / view switch / tab switch, and deletes drafts for
 * docs that become clean or close.
 */
export function useCrashSafeDrafts(platform: PlatformBridge): void {
  const session = platform.session;
  const activeId = useDocStore((s) => s.activeId);
  const activeText = useDocStore((s) => s.activeDoc()?.text);
  const activeDirty = useDocStore((s) => s.activeDoc()?.dirty ?? false);
  const viewMode = useDocStore((s) => s.viewMode);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const writeActiveNow = useRef<() => void>(() => {});
  writeActiveNow.current = () => {
    if (!session) return;
    const s = useDocStore.getState();
    const doc = s.docs.find((d) => d.id === s.activeId);
    if (!doc) return;
    const t = draftWriteTargets({ id: doc.id, text: doc.text, dirty: doc.dirty, kind: doc.kind });
    if (!t) return;
    void session.writeDraft(t.draftId, t.text).catch(() => {});
  };

  // Register a flush used by attemptClose.
  useEffect(() => registerFlusher(() => writeActiveNow.current()), []);

  // Debounced write on edit.
  useEffect(() => {
    if (!session) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => writeActiveNow.current(), DRAFT_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [session, activeId, activeText, activeDirty]);

  // Immediate flush on view switch / tab switch.
  const prevActive = useRef(activeId);
  useEffect(() => {
    if (prevActive.current !== activeId) {
      writeActiveNow.current();
      prevActive.current = activeId;
    }
  }, [activeId]);
  const prevView = useRef(viewMode);
  useEffect(() => {
    if (prevView.current !== viewMode) {
      writeActiveNow.current();
      prevView.current = viewMode;
    }
  }, [viewMode]);

  // Immediate flush on blur / hidden.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onBlur = () => writeActiveNow.current();
    const onVis = () => {
      if (document.hidden) writeActiveNow.current();
    };
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Delete drafts for docs that left the dirty set (saved or closed).
  const prevDirty = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!session) return;
    const unsub = useDocStore.subscribe((state) => {
      const nextDirty = new Set(state.docs.filter((d) => d.dirty).map((d) => d.id));
      const toDelete = diffDraftDeletions(prevDirty.current, nextDirty);
      prevDirty.current = nextDirty;
      for (const draftId of toDelete) void session.deleteDraft(draftId).catch(() => {});
    });
    return unsub;
  }, [session]);
}
